import clsx from 'clsx'
import React, { CSSProperties, FC } from 'react'

import CloseIcon from '@Assets/SVG/Close'
import { Button } from '@Components/Button'
import { SVGIcon } from '@Components/Icons'
import { ErrorContent } from '@Components/Modal/Dialog/ErrorContent'
import { SuccessContent } from '@Components/Modal/Dialog/SuccessContent'
import { useTranslation } from '@Globals/Translations'

import styles from './Dialog.module.scss'

export interface DialogProps {
  ID?: string | null
  button?: 'close' | 'okay'
  children?: React.ReactNode
  easyClose?: boolean
  hideCloseButton?: boolean
  maxWidth?: string
  onHide?: () => void
  title?: string
  variant?: 'default' | 'error' | 'success' | 'sadError'
}

export type ContentProps = Pick<DialogProps, 'children' | 'title' | 'variant'>

const Content: FC<ContentProps> = ({ children, variant, ...props }) => {
  if (variant === 'success') {
    return <SuccessContent {...props}>{children}</SuccessContent>
  }
  if (variant === 'error' || variant === 'sadError') {
    return <ErrorContent {...props} variant={variant}>{children}</ErrorContent>
  }
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>
}
/**
 * @deprecated The component should not be used - use NativeModal instead
 */
export const Dialog: FC<DialogProps> = ({
  ID = null,
  button,
  children,
  hideCloseButton = false,
  maxWidth,
  onHide,
  title,
  variant = 'default',
}: DialogProps) => {
  const { t } = useTranslation()

  const buttonText = {
    close: t('common:CLOSE'),
    okay: t('common:OKAY'),
  }

  const dialogWrapperID = ID ? `dialog-wrapper-${ID}` : ''
  const dialogBodyID = ID ? `dialog-body-${ID}` : ''

  return (
    <section
      className={styles.dialogWrapper}
      {...(dialogWrapperID ? { id: dialogWrapperID } : {})}
      onClick={e => e.stopPropagation()}
    >
      {!hideCloseButton && (
        <button aria-label={t('common:CLOSE')} className={styles.dialogCloseButton} onClick={onHide} type="button">
          <SVGIcon icon={CloseIcon} size="14px" />
        </button>
      )}
      <div
        {...(dialogBodyID ? { id: dialogBodyID } : {})}
        className={clsx(styles.dialogBody, hideCloseButton && styles.hideCloseButton, maxWidth && styles.maxWidth)}
        style={{ '--maxWidth': maxWidth } as CSSProperties}
      >
        <Content title={title} variant={variant}>{children}</Content>
      </div>
      {button && (
        <footer className={styles.dialogFooter}>
          <Button color="dark" onClick={onHide} type="button" variant="outlined">
            {buttonText[button]}
          </Button>
        </footer>
      )}
    </section>
  )
}
